import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  ShareNetwork,
  X, DownloadSimple, UploadSimple, LockKey, Archive, LinkSimple, Trash,
  ArrowCounterClockwise, ClockCounterClockwise, Spinner, Books,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { dmsApi, SIMPLE_STATUS_STYLE, fmtSize, fmtDate } from './dmsApi';
import ShareMenu from './ShareMenu';
import AddToBundleDialog from './AddToBundleDialog';
import DocReadPanel from './DocReadPanel';
import { OfficePreview } from './OfficePreview';
import { DocFileViewer } from './DocFileViewer';
import { folderOptions, folderPathLabel } from './folderTree';
import { formatJST } from '@/utils/datetime';

const ORIGIN_LABEL = {
  eng_asset_files: '技術資産から統合',
  mfg_attachments: '製造から統合',
  eng_equipment_docs: '設備から統合',
};


const Sel = ({ value, onChange, options, disabled, testId }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} data-testid={testId}
    className="h-8 w-full rounded-md border border-zinc-300 bg-white px-2 text-xs text-zinc-800 outline-none focus:ring-2 focus:ring-teal-200 disabled:bg-zinc-50 disabled:text-zinc-400">
    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

export default function DocDetailDrawer({ docId, meta, folders, onClose, onChanged, editOnly = false }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [projQ, setProjQ] = useState('');
  const [projResults, setProjResults] = useState([]);
  const [bundleAddOpen, setBundleAddOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [big, setBig] = useState(false);
  const urlRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await dmsApi.document(docId);
      setDetail(d);
      const doc = d.document;
      setForm({
        title: doc.title, description: doc.description || '', folder_id: doc.folder_id,
        doc_type: doc.doc_type, status: doc.status, confidentiality: doc.confidentiality,
        tags: (doc.tags || []).join(', '),
      });
      if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
      setPreviewUrl(null);
      const mime = doc.mime_type || '';
      if (doc.preview_allowed && meta.features?.preview_service &&
          (mime === 'application/pdf' || mime.startsWith('image/'))) {
        try {
          const { url } = await dmsApi.fileBlobUrl(docId);
          urlRef.current = url;
          setPreviewUrl(url);
        } catch { /* preview optional */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || '書類の取得に失敗しました');
      onClose?.();
    } finally { setLoading(false); }
  }, [docId, meta, onClose]);

  useEffect(() => {
    load();
    return () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); };
  }, [load]);

  const doc = detail?.document;
  const readOnly = doc?.read_only || doc?.is_deleted || !meta.can_edit;

  const saveMeta = async () => {
    setSaving(true);
    try {
      await dmsApi.updateDocument(docId, {
        title: form.title.trim(), description: form.description.trim() || null,
        folder_id: form.folder_id, doc_type: form.doc_type, status: form.status,
        confidentiality: form.confidentiality,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      });
      toast.success('メタデータを保存しました');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存に失敗しました');
    } finally { setSaving(false); }
  };

  const replaceFile = async (file) => {
    if (!file) return;
    setReplacing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await dmsApi.replaceFile(docId, fd);
      toast.success(`版を差し替えました（v${r.version_number}）— 旧版は履歴に保持されます`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '差し替えに失敗しました');
    } finally { setReplacing(false); }
  };

  const trash = async () => {
    try {
      await dmsApi.deleteDocument(docId);
      toast.success('ゴミ箱へ移動しました（復元可能）');
      onChanged?.(); onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || '削除に失敗しました'); }
  };

  const restore = async () => {
    try {
      await dmsApi.restoreDocument(docId);
      toast.success('復元しました');
      onChanged?.(); onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || '復元に失敗しました'); }
  };

  const searchProjects = async (q) => {
    setProjQ(q);
    if (!q.trim()) { setProjResults([]); return; }
    try { setProjResults(await dmsApi.projectsLookup(q)); } catch { setProjResults([]); }
  };

  const addLink = async (pid) => {
    try {
      await dmsApi.addLink(docId, { target_type: 'project', target_id: pid });
      toast.success('プロジェクトへ連携しました');
      setProjQ(''); setProjResults([]);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || '連携に失敗しました'); }
  };

  return (
    <Sheet open onOpenChange={(o) => { if (!o && !shareOpen) onClose?.(); }}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="dms-detail-drawer">
        {loading || !doc ? (
          <div className="py-20 text-center text-sm text-zinc-400">読み込み中...</div>
        ) : (
          <div className="space-y-4">
            <SheetHeader>
              <SheetTitle className="text-base flex items-center gap-2 flex-wrap pr-6">
                <span className="font-mono text-xs text-zinc-400">{doc.document_number}</span>
                <span className="min-w-0">{doc.title}</span>
              </SheetTitle>
            </SheetHeader>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className={`px-2 py-0.5 rounded border text-[11px] font-semibold ${SIMPLE_STATUS_STYLE[doc.status_simple] || ''}`}
                data-testid="dms-detail-status">{doc.status_simple_label}</span>
              <span className="px-2 py-0.5 rounded border border-zinc-200 bg-zinc-50 text-zinc-600 text-[11px]">{doc.doc_type_label}</span>
              {doc.is_archived && (
                <span className="px-2 py-0.5 rounded border border-stone-300 bg-stone-100 text-stone-600 text-[11px] inline-flex items-center gap-1"
                  data-testid="dms-detail-archive-badge">
                  <Archive size={11} weight="fill" /> アーカイブ{doc.read_only ? '（読取専用）' : ''}
                </span>
              )}
              {doc.origin?.collection && (
                <span className="px-2 py-0.5 rounded border border-teal-200 bg-teal-50 text-teal-700 text-[11px]"
                  title={`出所: ${doc.origin.collection} / ${doc.origin.record_id || ''}（${doc.origin.adopted_at || doc.origin_adopted_at || ''}）`}
                  data-testid="dms-detail-origin">
                  {ORIGIN_LABEL[doc.origin.collection] || 'モジュール統合'}
                  {doc.origin.record_label ? ` · ${doc.origin.record_label}` : ''}
                </span>
              )}
              {['restricted', 'confidential'].includes(doc.confidentiality) && (
                <span className="px-2 py-0.5 rounded border border-rose-200 bg-rose-50 text-rose-700 text-[11px] inline-flex items-center gap-1">
                  <LockKey size={11} weight="fill" /> {doc.confidentiality === 'confidential' ? '社外秘' : '制限付き'}
                </span>
              )}
            </div>

            {/* U6 — 編集専用（ビューアーから「詳細編集」で開いたとき）は
                プレビューとダウンロードを出さない。同じものが2つ並ぶのをやめる。 */}
            {!editOnly && (
            <div className="relative rounded-lg border border-zinc-200 bg-zinc-50 overflow-hidden" data-testid="dms-detail-preview">
              {doc.preview_allowed && (
                <button type="button" onClick={() => setBig(true)}
                  className="absolute right-2 top-2 z-10 inline-flex items-center gap-1 rounded-md border border-zinc-300 bg-white/90 px-2 py-1 text-[11px] text-zinc-700 shadow-sm hover:bg-white"
                  data-testid="dms-detail-enlarge">拡大して見る</button>
              )}
              {!doc.preview_allowed ? (
                <div className="py-12 text-center text-zinc-400">
                  <LockKey size={32} weight="duotone" className="mx-auto mb-2" />
                  <p className="text-xs">保護された書類のためプレビューできません</p>
                </div>
              ) : previewUrl && doc.mime_type === 'application/pdf' ? (
                <iframe title="preview" src={previewUrl} className="w-full h-[420px] bg-white" />
              ) : previewUrl ? (
                <img src={previewUrl} alt={doc.title} onClick={() => setBig(true)}
                  className="max-h-[420px] mx-auto object-contain cursor-zoom-in" />
              ) : (
                <OfficePreview docId={doc.id} doc={doc} heightClass="h-[420px]" />
              )}
            </div>
            )}
            {big && !editOnly && (
              <DocFileViewer documentId={docId} filename={doc.current_filename}
                mime={doc.mime_type} title={doc.title} thumbUrl={previewUrl}
                onClose={() => setBig(false)} />
            )}
            <div className="flex items-center gap-2 flex-wrap">
              {doc.preview_allowed && !editOnly && (
                <Button size="sm" variant="outline" className="h-8 text-xs"
                  onClick={() => dmsApi.downloadFile(docId, null, doc.current_filename)}
                  data-testid="dms-detail-download">
                  <DownloadSimple size={14} className="mr-1" /> ダウンロード
                </Button>
              )}
              {!readOnly && (
                <label className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-zinc-300 text-xs cursor-pointer hover:bg-zinc-50">
                  {replacing ? <Spinner size={14} className="animate-spin" /> : <UploadSimple size={14} />}
                  版を差し替え
                  <input type="file" className="hidden" data-testid="dms-detail-replace"
                    onChange={(e) => { replaceFile(e.target.files?.[0]); e.target.value = ''; }} disabled={replacing} />
                </label>
              )}
              {doc.is_deleted ? (
                <Button size="sm" variant="outline" className="h-8 text-xs text-emerald-700 border-emerald-300"
                  onClick={restore} data-testid="dms-detail-restore">
                  <ArrowCounterClockwise size={14} className="mr-1" /> 復元
                </Button>
              ) : (!doc.read_only && meta.can_edit && (
                <Button size="sm" variant="outline" className="h-8 text-xs text-rose-600 border-rose-300"
                  onClick={trash} data-testid="dms-detail-delete">
                  <Trash size={14} className="mr-1" /> ゴミ箱へ
                </Button>
              ))}
              {!doc.is_deleted && (
                <Button size="sm" variant="outline" className="h-8 text-xs"
                  onClick={() => setShareOpen(true)} data-testid="dms-detail-share">
                  <ShareNetwork size={14} className="mr-1" /> 共有
                </Button>
              )}
              {meta.can_edit && !doc.is_deleted && meta.features?.bundles_binders_envelopes && (
                <Button size="sm" variant="outline"
                  className="h-8 text-xs text-amber-700 border-amber-300"
                  onClick={() => setBundleAddOpen(true)} data-testid="dms-detail-add-bundle">
                  <Books size={14} className="mr-1" /> バインダーに追加
                </Button>
              )}
            </div>

            {/* Metadata */}
            <div className="rounded-lg border border-zinc-200 p-3 space-y-2.5" data-testid="dms-detail-meta">
              <p className="text-xs font-bold text-zinc-700">メタデータ</p>
              {doc.read_only && (
                <p className="text-[11px] text-stone-600 bg-stone-50 border border-stone-200 rounded px-2 py-1">
                  アーカイブ書類（読取専用）のため編集できません
                </p>
              )}
              <div>
                <Label className="text-[10px] font-semibold text-zinc-500">タイトル</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  disabled={readOnly} className="h-8 text-xs mt-0.5" data-testid="dms-meta-title" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] font-semibold text-zinc-500">フォルダー</Label>
                  <div className="mt-0.5"><Sel value={form.folder_id} onChange={(v) => setForm({ ...form, folder_id: v })}
                    disabled={readOnly} testId="dms-meta-folder"
                    options={folderOptions(folders)} />
                    {form.folder_id && (
                      <p className="mt-0.5 text-[10px] text-zinc-400" data-testid="dms-meta-folder-path">
                        {folderPathLabel(folders, form.folder_id)}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] font-semibold text-zinc-500">書類種別</Label>
                  <div className="mt-0.5"><Sel value={form.doc_type} onChange={(v) => setForm({ ...form, doc_type: v })}
                    disabled={readOnly} options={meta.doc_types} testId="dms-meta-type" /></div>
                </div>
                <div>
                  <Label className="text-[10px] font-semibold text-zinc-500">ステータス</Label>
                  <div className="mt-0.5"><Sel value={form.status} onChange={(v) => setForm({ ...form, status: v })}
                    disabled={readOnly} options={meta.statuses} testId="dms-meta-status" /></div>
                </div>
                <div>
                  <Label className="text-[10px] font-semibold text-zinc-500">機密区分</Label>
                  <div className="mt-0.5"><Sel value={form.confidentiality} onChange={(v) => setForm({ ...form, confidentiality: v })}
                    disabled={readOnly} options={meta.confidentiality} testId="dms-meta-conf" /></div>
                </div>
              </div>
              <div>
                <Label className="text-[10px] font-semibold text-zinc-500">説明</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  disabled={readOnly} className="h-8 text-xs mt-0.5" data-testid="dms-meta-desc" />
              </div>
              <div>
                <Label className="text-[10px] font-semibold text-zinc-500">タグ（カンマ区切り）</Label>
                <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  disabled={readOnly} className="h-8 text-xs mt-0.5" data-testid="dms-meta-tags" />
              </div>
              <div className="text-[11px] text-zinc-500 grid grid-cols-2 gap-x-3 gap-y-0.5">
                <span>所有者: {doc.owner_name}</span>
                <span>サイズ: {fmtSize(doc.file_size)}</span>
                <span>取得元: {(meta.source_types.find((s) => s.value === doc.source_type) || {}).label || doc.source_type}</span>
                <span>更新: {fmtDate(doc.updated_at)}</span>
              </div>
              {!readOnly && (
                <Button size="sm" onClick={saveMeta} disabled={saving}
                  className="h-8 text-xs bg-teal-700 hover:bg-teal-800" data-testid="dms-meta-save">
                  {saving ? '保存中...' : 'メタデータを保存'}
                </Button>
              )}
            </div>

            {/* L8/L9 — 読み直し・読み取った内容の手直し（詳細でもできる） */}
            {!editOnly && meta.features?.ocr_text_search && (
              <div className="rounded-lg border border-zinc-200 p-3" data-testid="dms-detail-read">
                <p className="text-xs font-bold text-zinc-700 mb-2">読み取り内容</p>
                <DocReadPanel documentId={docId}
                  canEdit={!!meta.can_edit && !doc.is_deleted && !doc.read_only}
                  onChanged={() => { load(); onChanged?.(); }} />
              </div>
            )}

            {/* Project links */}
            {meta.features?.project_linking && (
              <div className="rounded-lg border border-zinc-200 p-3 space-y-2" data-testid="dms-detail-links">
                <p className="text-xs font-bold text-zinc-700 flex items-center gap-1">
                  <LinkSimple size={13} /> 関連レコード
                </p>
                {(detail.links || []).map((lk) => (
                  <div key={lk.id} className="flex items-center justify-between gap-2 text-xs bg-indigo-50/60 border border-indigo-100 rounded px-2 py-1.5"
                    data-testid={`dms-link-${lk.id}`}>
                    <span className="text-[9px] font-bold text-indigo-500 bg-white border border-indigo-200 rounded px-1 py-0.5 shrink-0">
                      {lk.target_type_label || lk.target_type}
                    </span>
                    <span className="text-indigo-800 font-medium truncate flex-1">{lk.target_label}</span>
                    {meta.can_edit && !doc.is_deleted && (
                      <button type="button" className="text-zinc-400 hover:text-rose-600"
                        onClick={async () => { await dmsApi.removeLink(docId, lk.id); load(); }}
                        data-testid={`dms-unlink-${lk.id}`}>
                        <X size={12} weight="bold" />
                      </button>
                    )}
                  </div>
                ))}
                {meta.can_edit && !doc.is_deleted && !doc.read_only && (
                  <div className="relative">
                    <Input value={projQ} onChange={(e) => searchProjects(e.target.value)}
                      placeholder="プロジェクト名で検索して連携..." className="h-8 text-xs" data-testid="dms-link-search" />
                    {projResults.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full rounded-md border border-zinc-200 bg-white shadow-lg max-h-40 overflow-y-auto">
                        {projResults.map((p) => (
                          <button key={p.id} type="button" onClick={() => addLink(p.id)}
                            className="block w-full text-left px-2.5 py-1.5 text-xs hover:bg-teal-50"
                            data-testid={`dms-link-result-${p.id}`}>
                            {p.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Versions */}
            {doc.preview_allowed && (
              <div className="rounded-lg border border-zinc-200 p-3 space-y-1.5" data-testid="dms-detail-versions">
                <p className="text-xs font-bold text-zinc-700">版履歴（{detail.versions.length}版）</p>
                {detail.versions.map((v) => (
                  <div key={v.id} className="flex items-center justify-between text-[11px] py-1 border-b border-zinc-200 last:border-0"
                    data-testid={`dms-version-${v.version_number}`}>
                    <span className="flex items-center gap-2 min-w-0">
                      <span className={`px-1.5 py-0.5 rounded font-mono font-bold ${v.is_current ? 'bg-teal-100 text-teal-700' : 'bg-zinc-100 text-zinc-500'}`}>
                        v{v.version_number}
                      </span>
                      <span className="truncate text-zinc-600">{v.filename}</span>
                    </span>
                    <span className="flex items-center gap-2 shrink-0 text-zinc-400">
                      {fmtDate(v.uploaded_at)} · {v.uploaded_by}
                      <button type="button" className="text-zinc-400 hover:text-teal-600"
                        onClick={() => dmsApi.downloadFile(docId, v.id, v.filename)}
                        data-testid={`dms-version-dl-${v.version_number}`}>
                        <DownloadSimple size={13} />
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Activity */}
            <div className="rounded-lg border border-zinc-200 p-3 space-y-1" data-testid="dms-detail-activity">
              <p className="text-xs font-bold text-zinc-700 flex items-center gap-1">
                <ClockCounterClockwise size={13} /> アクティビティ
              </p>
              {(detail.activity || []).map((a) => (
                <div key={a.id} className="text-[11px] text-zinc-500 flex items-center gap-2 py-0.5">
                  <span className="font-mono text-zinc-400 shrink-0">{formatJST(a.at)}</span>
                  <span className="font-semibold text-zinc-600">{a.actor_name}</span>
                  <span>{ACTIVITY_LABELS[a.action] || a.action}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      <ShareMenu open={shareOpen} onClose={() => setShareOpen(false)}
        documentIds={docId ? [docId] : []} isAdmin={!!meta?.is_admin} />
      </SheetContent>
      <AddToBundleDialog open={bundleAddOpen} documentId={docId}
        onClose={() => setBundleAddOpen(false)}
        onDone={() => { setBundleAddOpen(false); load(); onChanged?.(); }} />
    </Sheet>
  );
}

const ACTIVITY_LABELS = {
  upload: 'アップロード', replace_version: '版差し替え', edit_metadata: 'メタデータ編集',
  move_folder: 'フォルダー移動', delete: 'ゴミ箱へ移動', restore: '復元', purge: '完全削除',
  link: 'レコード連携', unlink: '連携解除', folder_create: 'フォルダー作成',
  folder_update: 'フォルダー更新', folder_delete: 'フォルダー削除',
  tag_create: 'タグ作成', tag_delete: 'タグ削除', feature_toggle: '機能切替',
};
